import {
  Calendar,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  DollarSign,
  FileText,
  User,
  XCircle,
} from 'lucide-react';
import type React from 'react';
import type { CreditAnalysisHistory } from '@/api/commercial/credit-analysis';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  SEM_RESTRICAO: 'Sem restrição',
  APENAS_A_VISTA: 'Somente à vista',
};

interface CreditAnalysisHistoryListProps {
  historyData: {
    docs: CreditAnalysisHistory[];
    total: number;
    pages: number;
    limit: number;
  };
  page: number;
  onPageChange: (page: number) => void;
}

const verdictConfig: Record<
  'APPROVED' | 'PARTIALLY_APPROVED' | 'DECLINED',
  {
    title: string;
    icon: React.ComponentType<{ className?: string }>;
    badgeVariant: 'success-subtle' | 'warning-subtle' | 'destructive-subtle';
  }
> = {
  APPROVED: {
    title: 'Aprovada',
    icon: CheckCircle,
    badgeVariant: 'success-subtle',
  },
  PARTIALLY_APPROVED: {
    title: 'Parcialmente Aprovada',
    icon: CheckCircle,
    badgeVariant: 'warning-subtle',
  },
  DECLINED: {
    title: 'Reprovada',
    icon: XCircle,
    badgeVariant: 'destructive-subtle',
  },
};

export function CreditAnalysisHistoryList({
  historyData,
  page,
  onPageChange,
}: CreditAnalysisHistoryListProps) {
  const formatDate = (date: string | 'NEXT_PURCHASE' | undefined) => {
    if (date === 'NEXT_PURCHASE') {
      return 'Na próxima compra';
    }
    if (!date) return '-';
    try {
      const parsed = new Date(date);
      if (Number.isNaN(parsed.getTime())) {
        return '-';
      }
      return parsed.toLocaleDateString('pt-BR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return '-';
    }
  };

  const formatCurrency = (cents: number | undefined) => {
    if (cents == null || cents === 0) return '-';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(cents / 100);
  };

  const handlePreviousPage = () => {
    if (page > 1) {
      onPageChange(page - 1);
    }
  };

  const handleNextPage = () => {
    if (page < historyData.pages) {
      onPageChange(page + 1);
    }
  };

  if (!historyData.docs || historyData.docs.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>Nenhuma análise anterior encontrada</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          Mostrando {historyData.docs.length} de {historyData.total} análises
        </p>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handlePreviousPage} disabled={page === 1}>
            <ChevronLeft className="h-4 w-4" />
            Anterior
          </Button>
          <span className="text-sm text-gray-600">
            Página {page} de {historyData.pages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={handleNextPage}
            disabled={page === historyData.pages}
          >
            Próxima
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {historyData.docs.map((item, index) => {
        const config =
          verdictConfig[item.status as keyof typeof verdictConfig] || verdictConfig.APPROVED;
        const StatusIcon = config.icon;
        const isLast = index === historyData.docs.length - 1;

        return (
          <Card key={item.id} className="border border-gray-200 shadow-sm">
            {/* Header — sem alterações */}
            <CardHeader className="pb-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <StatusIcon className="h-5 w-5 text-gray-500" />
                  <div>
                    <CardTitle className="text-base font-semibold text-gray-900">
                      Análise de Crédito
                    </CardTitle>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant={config.badgeVariant}>{config.title}</Badge>
                      <span className="text-xs text-gray-500">{formatDate(item.analyzedAt)}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <User className="h-4 w-4" />
                  <span>Analista: {item.analystName}</span>
                </div>
              </div>
            </CardHeader>

            {/* Content — grid de 2 colunas, todos os campos lado a lado */}
            <CardContent className="grid grid-cols-2 gap-3">
              {/* Formas de Pagamento */}
              {item.paymentMethods && item.paymentMethods.length > 0 && (
                <div className="flex items-start gap-3">
                  <CreditCard className="h-4 w-4 text-gray-500 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-xs font-medium text-gray-600">Formas de Pagamento</p>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {item.paymentMethods.map((method) => (
                        <span
                          key={method}
                          className="px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200"
                        >
                          {PAYMENT_METHOD_LABELS[method] || method}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Limite de Crédito */}
              {(item.status === 'APPROVED' || item.status === 'PARTIALLY_APPROVED') && (
                <div className="flex items-start gap-3">
                  <DollarSign className="h-4 w-4 text-gray-500 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-xs font-medium text-gray-600">Limite de Crédito</p>
                    <p className="text-sm font-semibold text-gray-900">
                      {formatCurrency(item.creditLimitInCents)}
                    </p>
                  </div>
                </div>
              )}

              {/* Limite Disponível */}
              {(item.status === 'APPROVED' || item.status === 'PARTIALLY_APPROVED') && (
                <div className="flex items-start gap-3">
                  <DollarSign className="h-4 w-4 text-gray-500 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-xs font-medium text-gray-600">Limite Disponível</p>
                    <p className="text-sm font-semibold text-gray-900">
                      {formatCurrency(item.creditAvailableInCents)}
                    </p>
                  </div>
                </div>
              )}

              {/* Válido até */}
              {item.newAnalysisIn && (
                <div className="flex items-start gap-3">
                  <Calendar className="h-4 w-4 text-gray-500 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-xs font-medium text-gray-600">Válido até</p>
                    <p className="text-sm text-gray-700">{formatDate(item.newAnalysisIn)}</p>
                  </div>
                </div>
              )}

              {/* Observações */}
              <div className="flex items-start gap-3">
                <FileText className="h-4 w-4 text-gray-500 mt-0.5" />
                <div className="flex-1">
                  <p className="text-xs font-medium text-gray-600">Observações</p>
                  <p className="text-sm text-gray-700 mt-0.5">{item.observations}</p>
                </div>
              </div>
            </CardContent>

            {!isLast && <Separator className="mt-4" />}
          </Card>
        );
      })}
    </div>
  );
}
